import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import {
  CheckCircle, XCircle, Clock, Plus, Download, X, Calendar, Search, Eye, History, RefreshCw, Building2, Users
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ApplicationsTab({ token }) {
  const [apps, setApps] = useState([]);
  const [filteredApps, setFilteredApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [selectedApps, setSelectedApps] = useState([]);
  const [changeHistory, setChangeHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [activeCenters, setActiveCenters] = useState([]);
  const [activeExaminers, setActiveExaminers] = useState([]);
  const [processing, setProcessing] = useState(false);
  
  // Filter states
  const [filters, setFilters] = useState({
    status: 'all',
    center: 'all',
    preferredCenter: 'all', // New filter for candidate's preferred examination center
    examiner: 'all',
    examType: 'all',
    institution: 'all', // Filter for nominating institution
    dateFrom: '',
    dateTo: '',
    search: '',
    sortOrder: 'latest'
  });
  
  const [allocationForm, setAllocationForm] = useState({
    examination_center_id: '',
    examiner_id: '',
    examination_date: '',
    examination_time: '',
    examination_type: '',
    admin_notes: ''
  });
  const [rescheduleForm, setRescheduleForm] = useState({
    examination_center_id: '',
    examiner_id: '',
    examination_date: '',
    examination_time: '',
    examination_type: '',
    reason: ''
  });
  const [bulkForm, setBulkForm] = useState({
    examination_center_id: '',
    examiner_id: '',
    examination_date: '',
    examination_time: '',
    examination_type: '',
    admin_notes: ''
  });

  const examTypes = ['OSCE', 'Written', 'Viva', 'Hybrid'];
  const statusOptions = ['all', 'submitted', 'under_review', 'scheduled', 'rescheduled', 'completed', 'rejected'];

  const fetchApps = async () => {
    try {
      const res = await axios.get(`${API}/admin/applications`, { headers: { Authorization: `Bearer ${token}` } });
      setApps(res.data);
      setFilteredApps(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchDropdownData = async () => {
    try {
      const [centersRes, examinersRes] = await Promise.all([
        axios.get(`${API}/admin/active-examination-centers`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/admin/active-examiners`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setActiveCenters(centersRes.data);
      setActiveExaminers(examinersRes.data);
    } catch (err) { console.error(err); }
  };

  // Apply filters
  useEffect(() => {
    let result = [...apps];
    
    // Search filter
    if (filters.search) {
      const term = filters.search.toLowerCase();
      result = result.filter(a => 
        a.full_name?.toLowerCase().includes(term) ||
        a.email?.toLowerCase().includes(term) ||
        a.examination?.toLowerCase().includes(term)
      );
    }
    
    if (filters.status !== 'all') {
      result = result.filter(a => a.status === filters.status);
    }
    if (filters.center !== 'all') {
      result = result.filter(a => a.examination_center_id === filters.center);
    }
    if (filters.preferredCenter !== 'all') {
      result = result.filter(a => a.preferred_examination_center_id === filters.preferredCenter);
    }
    if (filters.examiner !== 'all') {
      result = result.filter(a => a.examiner_id === filters.examiner);
    }
    if (filters.examType !== 'all') {
      result = result.filter(a => a.examination_type === filters.examType);
    }
    if (filters.institution !== 'all') {
      if (filters.institution === 'direct') {
        result = result.filter(a => !a.institution_name);
      } else {
        result = result.filter(a => a.institution_name === filters.institution);
      }
    }
    if (filters.dateFrom) {
      result = result.filter(a => a.examination_date >= filters.dateFrom);
    }
    if (filters.dateTo) {
      result = result.filter(a => a.examination_date <= filters.dateTo);
    }
    
    // Sort by date
    result.sort((a, b) => {
      const dateA = new Date(a.created_at || a.applied_date);
      const dateB = new Date(b.created_at || b.applied_date);
      return filters.sortOrder === 'latest' ? dateB - dateA : dateA - dateB;
    });
    
    setFilteredApps(result);
  }, [apps, filters]);

  useEffect(() => { 
    fetchApps(); 
    fetchDropdownData();
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectApp = (appId) => {
    setSelectedApps(prev => 
      prev.includes(appId) 
        ? prev.filter(id => id !== appId)
        : [...prev, appId]
    );
  };

  const handleSelectAll = (apps) => {
    const pendingApps = apps.filter(a => ['submitted', 'under_review'].includes(a.status));
    if (selectedApps.length === pendingApps.length) {
      setSelectedApps([]);
    } else {
      setSelectedApps(pendingApps.map(a => a.id));
    }
  };

  const handleBulkAllocate = async (e) => {
    e.preventDefault();
    if (selectedApps.length === 0) {
      toast.error('No applications selected');
      return;
    }
    if (!bulkForm.examination_center_id || !bulkForm.examiner_id || 
        !bulkForm.examination_date || !bulkForm.examination_type) {
      toast.error('Please fill all mandatory fields');
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(
        `${API}/admin/applications/bulk-allocate`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: {
            application_ids: selectedApps,
            ...bulkForm
          }
        }
      );
      toast.success(res.data.message);
      setShowBulkModal(false);
      setSelectedApps([]);
      setBulkForm({
        examination_center_id: '',
        examiner_id: '',
        examination_date: '',
        examination_time: '',
        examination_type: '',
        admin_notes: ''
      });
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk allocation failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleExport = async (type = 'applications') => {
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.center !== 'all') params.append('center_id', filters.center);
      if (filters.examiner !== 'all') params.append('examiner_id', filters.examiner);
      if (filters.examType !== 'all') params.append('exam_type', filters.examType);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      
      const endpoint = type === 'allocations' ? 'allocations/export' : 'applications/export';
      const res = await axios.get(`${API}/admin/${endpoint}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Download file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} exported successfully`);
    } catch (err) {
      toast.error('Export failed');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      center: 'all',
      preferredCenter: 'all',
      examiner: 'all',
      examType: 'all',
      institution: 'all',
      dateFrom: '',
      dateTo: '',
      search: '',
      sortOrder: 'latest'
    });
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/applications/${id}`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Application ${status}`);
      fetchApps();
    } catch (err) { toast.error('Failed to update'); }
  };

  const handleOpenAllocationModal = (app) => {
    setSelectedApp(app);
    setAllocationForm({
      examination_center_id: '',
      examiner_id: '',
      examination_date: '',
      examination_time: '',
      examination_type: '',
      admin_notes: ''
    });
    setShowAllocationModal(true);
  };

  const handleOpenRescheduleModal = (app) => {
    setSelectedApp(app);
    setRescheduleForm({
      examination_center_id: app.examination_center_id || '',
      examiner_id: app.examiner_id || '',
      examination_date: app.examination_date || '',
      examination_time: app.examination_time || '',
      examination_type: app.examination_type || '',
      reason: ''
    });
    setShowRescheduleModal(true);
  };

  const handleApproveWithAllocation = async (e) => {
    e.preventDefault();
    if (!allocationForm.examination_center_id || !allocationForm.examiner_id || 
        !allocationForm.examination_date || !allocationForm.examination_type) {
      toast.error('Please fill all mandatory fields');
      return;
    }
    setProcessing(true);
    try {
      await axios.post(
        `${API}/admin/applications/${selectedApp.id}/approve-with-allocation`,
        allocationForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Application approved and examination scheduled');
      setShowAllocationModal(false);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await axios.put(
        `${API}/admin/applications/${selectedApp.id}/reschedule`,
        rescheduleForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Examination rescheduled successfully');
      setShowRescheduleModal(false);
      fetchApps();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setProcessing(false);
    }
  };

  const handleViewHistory = async (app) => {
    setSelectedApp(app);
    setLoadingHistory(true);
    setShowHistoryModal(true);
    try {
      const res = await axios.get(
        `${API}/admin/applications/${app.id}/change-history`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChangeHistory(res.data);
    } catch (err) {
      toast.error('Failed to load change history');
      setChangeHistory(null);
    } finally {
      setLoadingHistory(false);
    }
  };

  const statusColor = (s) => {
    const m = { 
      submitted: 'bg-blue-100 text-blue-700', 
      approved: 'bg-green-100 text-green-700', 
      scheduled: 'bg-emerald-100 text-emerald-700',
      rescheduled: 'bg-amber-100 text-amber-700',
      completed: 'bg-purple-100 text-purple-700',
      result_published: 'bg-indigo-100 text-indigo-700',
      rejected: 'bg-red-100 text-red-700', 
      under_review: 'bg-amber-100 text-amber-700' 
    };
    return m[s] || 'bg-slate-100 text-slate-700';
  };

  if (loading) return <p className="text-sm text-slate-500 py-8 text-center">Loading applications...</p>;

  const pendingApps = filteredApps.filter(a => ['submitted', 'under_review'].includes(a.status));

  return (
    <div data-testid="admin-applications-tab">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="p-3">
          <p className="text-xs text-slate-500">Total</p>
          <p className="text-xl font-bold text-[#1B2A4A]">{apps.length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-slate-500">Pending</p>
          <p className="text-xl font-bold text-blue-600">{apps.filter(a => a.status === 'submitted').length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-slate-500">Scheduled</p>
          <p className="text-xl font-bold text-emerald-600">{apps.filter(a => a.status === 'scheduled' || a.status === 'rescheduled').length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-slate-500">Completed</p>
          <p className="text-xl font-bold text-purple-600">{apps.filter(a => a.status === 'completed').length}</p>
        </Card>
        <Card className="p-3">
          <p className="text-xs text-slate-500">Rejected</p>
          <p className="text-xl font-bold text-red-600">{apps.filter(a => a.status === 'rejected').length}</p>
        </Card>
      </div>

      {/* Filters and Actions Bar */}
      <div className="bg-slate-50 p-3 rounded-lg mb-4">
        <div className="flex flex-wrap gap-2 items-end">
          {/* Search */}
          <div className="min-w-[200px]">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
              <Input
                placeholder="Name, Email, Phone..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                className="h-8 text-xs rounded-sm mt-1 pl-7"
                data-testid="apps-search"
              />
            </div>
          </div>

          {/* Sort Order */}
          <div className="min-w-[120px]">
            <Label className="text-xs">Sort</Label>
            <Select value={filters.sortOrder} onValueChange={(v) => setFilters({...filters, sortOrder: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="latest">Latest First</SelectItem>
                <SelectItem value="oldest">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="min-w-[120px]">
            <Label className="text-xs">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({...filters, status: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(s => (
                  <SelectItem key={s} value={s}>{s === 'all' ? 'All Status' : s.replace('_', ' ')}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Preferred Center Filter - Candidate's selected center during application */}
          <div className="min-w-[150px]">
            <Label className="text-xs">Preferred Center</Label>
            <Select value={filters.preferredCenter} onValueChange={(v) => setFilters({...filters, preferredCenter: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Preferred</SelectItem>
                {activeCenters.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Allocated Center Filter */}
          <div className="min-w-[150px]">
            <Label className="text-xs">Allocated Center</Label>
            <Select value={filters.center} onValueChange={(v) => setFilters({...filters, center: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Allocated</SelectItem>
                {activeCenters.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Examiner Filter */}
          <div className="min-w-[150px]">
            <Label className="text-xs">Examiner</Label>
            <Select value={filters.examiner} onValueChange={(v) => setFilters({...filters, examiner: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Examiners</SelectItem>
                {activeExaminers.map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Exam Type Filter */}
          <div className="min-w-[100px]">
            <Label className="text-xs">Exam Type</Label>
            <Select value={filters.examType} onValueChange={(v) => setFilters({...filters, examType: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {examTypes.map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Institution Filter */}
          <div className="min-w-[120px]">
            <Label className="text-xs">Institution</Label>
            <Select value={filters.institution} onValueChange={(v) => setFilters({...filters, institution: v})}>
              <SelectTrigger className="h-8 text-xs rounded-sm mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="direct">Direct Applications</SelectItem>
                {[...new Set(apps.filter(a => a.institution_name).map(a => a.institution_name))].map(inst => (
                  <SelectItem key={inst} value={inst}>{inst}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Date Range */}
          <div>
            <Label className="text-xs">Date From</Label>
            <Input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              className="h-8 text-xs rounded-sm mt-1 w-32"
            />
          </div>
          <div>
            <Label className="text-xs">Date To</Label>
            <Input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              className="h-8 text-xs rounded-sm mt-1 w-32"
            />
          </div>

          {/* Clear Filters */}
          <Button variant="outline" size="sm" onClick={clearFilters} className="h-8 text-xs rounded-sm">
            <X className="h-3 w-3 mr-1" />Clear
          </Button>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Bulk Actions */}
          {selectedApps.length > 0 && (
            <Button 
              size="sm" 
              className="h-8 bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm"
              onClick={() => setShowBulkModal(true)}
              data-testid="bulk-allocate-btn"
            >
              <Plus className="h-3 w-3 mr-1" />Bulk Allocate ({selectedApps.length})
            </Button>
          )}

          {/* Export */}
          <Button variant="outline" size="sm" onClick={() => handleExport('applications')} className="h-8 text-xs rounded-sm" data-testid="export-apps-btn">
            <Download className="h-3 w-3 mr-1" />Export CSV
          </Button>
        </div>
        
        {/* Filter Summary */}
        {filteredApps.length !== apps.length && (
          <p className="text-xs text-slate-500 mt-2">
            Showing {filteredApps.length} of {apps.length} applications
          </p>
        )}
      </div>

      {filteredApps.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center">
          {apps.length === 0 ? 'No applications yet.' : 'No applications match your filters.'}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8FAFC]">
                <TableHead className="w-8">
                  <input
                    type="checkbox"
                    checked={selectedApps.length > 0 && selectedApps.length === pendingApps.length}
                    onChange={() => handleSelectAll(filteredApps)}
                    className="rounded"
                    title="Select all pending applications"
                  />
                </TableHead>
                <TableHead className="text-xs font-bold">Applicant</TableHead>
                <TableHead className="text-xs font-bold">Institution</TableHead>
                <TableHead className="text-xs font-bold">Examination</TableHead>
                <TableHead className="text-xs font-bold">Preferred Center</TableHead>
                <TableHead className="text-xs font-bold">Registration</TableHead>
                <TableHead className="text-xs font-bold">Allocation</TableHead>
                <TableHead className="text-xs font-bold">Status</TableHead>
                <TableHead className="text-xs font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredApps.map((app) => (
                <TableRow key={app.id} className={selectedApps.includes(app.id) ? 'bg-blue-50' : ''}>
                  <TableCell>
                    {['submitted', 'under_review'].includes(app.status) && (
                      <input
                        type="checkbox"
                        checked={selectedApps.includes(app.id)}
                        onChange={() => handleSelectApp(app.id)}
                        className="rounded"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <p className="text-sm font-medium text-[#1B2A4A]">{app.full_name}</p>
                    <p className="text-xs text-slate-500">{app.email}</p>
                    <p className="text-xs text-slate-400">{app.nationality}</p>
                  </TableCell>
                  <TableCell>
                    {app.institution_name ? (
                      <div>
                        <Badge className="bg-purple-100 text-purple-700 text-xs">{app.institution_name}</Badge>
                        {app.is_institutional_nomination && (
                          <p className="text-xs text-purple-500 mt-1">Nominated</p>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400">Direct</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs rounded-sm">Route {app.route}</Badge>
                    <p className="text-xs text-slate-600 mt-1">{app.specialty || app.examination}</p>
                  </TableCell>
                  <TableCell>
                    {app.preferred_examination_center_name ? (
                      <div className="text-xs">
                        <p className="font-medium text-[#C5A572]">{app.preferred_examination_center_name}</p>
                        <p className="text-slate-400">{app.preferred_examination_center_city}</p>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Not specified</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 font-mono">{app.medical_reg_number || '-'}</TableCell>
                  <TableCell>
                    {app.examination_center_name ? (
                      <div className="text-xs">
                        <p className="font-medium text-[#1B2A4A]">{app.examination_center_name}</p>
                        <p className="text-slate-500">{app.examiner_name}</p>
                        <p className="text-slate-400">{app.examination_date} {app.examination_time && `@ ${app.examination_time}`}</p>
                        <Badge className="text-[10px] mt-1 bg-slate-100 text-slate-600">{app.examination_type}</Badge>
                      </div>
                    ) : (
                      <span className="text-xs text-slate-400 italic">Not allocated</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={`text-xs rounded-sm ${statusColor(app.status)}`} data-testid={`app-status-${app.id}`}>
                      {app.status?.replace('_', ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {/* Show Approve with Allocation for pending apps */}
                      {(app.status === 'submitted' || app.status === 'under_review') && (
                        <>
                          <Button 
                            size="sm" 
                            className="h-7 px-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm" 
                            onClick={() => handleOpenAllocationModal(app)} 
                            data-testid={`approve-allocate-${app.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />Approve & Allocate
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 px-2 text-xs rounded-sm border-amber-400 text-amber-600" 
                            onClick={() => updateStatus(app.id, 'under_review')} 
                            data-testid={`review-${app.id}`}
                          >
                            <Clock className="h-3 w-3 mr-1" />Review
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="h-7 px-2 text-xs rounded-sm border-red-300 text-red-600" 
                            onClick={() => updateStatus(app.id, 'rejected')} 
                            data-testid={`reject-${app.id}`}
                          >
                            <XCircle className="h-3 w-3 mr-1" />Reject
                          </Button>
                        </>
                      )}
                      {/* Show Reschedule for scheduled apps */}
                      {(app.status === 'scheduled' || app.status === 'rescheduled') && (
                        <>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 px-2 text-xs rounded-sm border-blue-400 text-blue-600" 
                            onClick={() => handleOpenRescheduleModal(app)} 
                            data-testid={`reschedule-${app.id}`}
                          >
                            <Calendar className="h-3 w-3 mr-1" />Reschedule
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="h-7 px-2 text-xs rounded-sm border-slate-400 text-slate-600" 
                            onClick={() => handleViewHistory(app)} 
                            data-testid={`history-${app.id}`}
                          >
                            <Clock className="h-3 w-3 mr-1" />History
                          </Button>
                          <Button 
                            size="sm" 
                            className="h-7 px-2 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-sm" 
                            onClick={() => updateStatus(app.id, 'completed')} 
                            data-testid={`complete-${app.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />Complete
                          </Button>
                        </>
                      )}
                      {app.status === 'completed' && (
                        <Button 
                          size="sm" 
                          className="h-7 px-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded-sm" 
                          onClick={() => updateStatus(app.id, 'result_published')} 
                          data-testid={`publish-${app.id}`}
                        >
                          <Award className="h-3 w-3 mr-1" />Publish Result
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Allocation Modal */}
      <Dialog open={showAllocationModal} onOpenChange={setShowAllocationModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Approve & Allocate Examination
            </DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <form onSubmit={handleApproveWithAllocation} className="space-y-4">
              {/* Candidate Info */}
              <div className="bg-slate-50 p-3 rounded-md">
                <p className="font-medium text-[#1B2A4A]">{selectedApp.full_name}</p>
                <p className="text-sm text-slate-500">{selectedApp.email}</p>
                <div className="flex gap-2 mt-1">
                  <Badge variant="outline" className="text-xs">Route {selectedApp.route}</Badge>
                  <Badge variant="outline" className="text-xs">{selectedApp.examination}</Badge>
                </div>
              </div>

              {/* Examination Center */}
              <div>
                <Label className="text-sm">Examination Center *</Label>
                <Select 
                  value={allocationForm.examination_center_id}
                  onValueChange={(v) => setAllocationForm({...allocationForm, examination_center_id: v})}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Select examination center" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCenters.map(center => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name} - {center.city}, {center.country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Examiner */}
              <div>
                <Label className="text-sm">Examiner *</Label>
                <Select 
                  value={allocationForm.examiner_id}
                  onValueChange={(v) => setAllocationForm({...allocationForm, examiner_id: v})}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Select examiner" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeExaminers.map(ex => (
                      <SelectItem key={ex.id} value={ex.id}>
                        {ex.full_name} ({ex.email})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">Examination Date *</Label>
                  <Input
                    type="date"
                    value={allocationForm.examination_date}
                    onChange={(e) => setAllocationForm({...allocationForm, examination_date: e.target.value})}
                    className="mt-1 rounded-sm"
                    required
                  />
                </div>
                <div>
                  <Label className="text-sm">Examination Time</Label>
                  <Input
                    type="time"
                    value={allocationForm.examination_time}
                    onChange={(e) => setAllocationForm({...allocationForm, examination_time: e.target.value})}
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>

              {/* Examination Type */}
              <div>
                <Label className="text-sm">Examination Type *</Label>
                <Select 
                  value={allocationForm.examination_type}
                  onValueChange={(v) => setAllocationForm({...allocationForm, examination_type: v})}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Select examination type" />
                  </SelectTrigger>
                  <SelectContent>
                    {examTypes.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Admin Notes */}
              <div>
                <Label className="text-sm">Admin Notes (Optional)</Label>
                <Textarea
                  value={allocationForm.admin_notes}
                  onChange={(e) => setAllocationForm({...allocationForm, admin_notes: e.target.value})}
                  placeholder="Internal notes..."
                  rows={2}
                  className="mt-1 rounded-sm"
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setShowAllocationModal(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" disabled={processing} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {processing ? 'Processing...' : 'Approve & Schedule'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reschedule Modal */}
      <Dialog open={showRescheduleModal} onOpenChange={setShowRescheduleModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Calendar className="h-5 w-5 text-blue-600" />
              Reschedule Examination
            </DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <form onSubmit={handleReschedule} className="space-y-4">
              {/* Current Allocation Info */}
              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                <p className="text-xs font-medium text-amber-700 mb-1">Current Allocation</p>
                <p className="text-sm text-[#1B2A4A]">{selectedApp.full_name}</p>
                <p className="text-xs text-slate-500">
                  {selectedApp.examination_center_name} • {selectedApp.examiner_name}
                </p>
                <p className="text-xs text-slate-500">
                  {selectedApp.examination_date} {selectedApp.examination_time && `@ ${selectedApp.examination_time}`}
                </p>
              </div>

              {/* Examination Center */}
              <div>
                <Label className="text-sm">New Examination Center</Label>
                <Select 
                  value={rescheduleForm.examination_center_id}
                  onValueChange={(v) => setRescheduleForm({...rescheduleForm, examination_center_id: v})}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Keep current or select new" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCenters.map(center => (
                      <SelectItem key={center.id} value={center.id}>
                        {center.name} - {center.city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Examiner */}
              <div>
                <Label className="text-sm">New Examiner</Label>
                <Select 
                  value={rescheduleForm.examiner_id}
                  onValueChange={(v) => setRescheduleForm({...rescheduleForm, examiner_id: v})}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Keep current or select new" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeExaminers.map(ex => (
                      <SelectItem key={ex.id} value={ex.id}>
                        {ex.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm">New Date</Label>
                  <Input
                    type="date"
                    value={rescheduleForm.examination_date}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, examination_date: e.target.value})}
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm">New Time</Label>
                  <Input
                    type="time"
                    value={rescheduleForm.examination_time}
                    onChange={(e) => setRescheduleForm({...rescheduleForm, examination_time: e.target.value})}
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>

              {/* Examination Type */}
              <div>
                <Label className="text-sm">Examination Type</Label>
                <Select 
                  value={rescheduleForm.examination_type}
                  onValueChange={(v) => setRescheduleForm({...rescheduleForm, examination_type: v})}
                >
                  <SelectTrigger className="mt-1 rounded-sm">
                    <SelectValue placeholder="Keep current or select new" />
                  </SelectTrigger>
                  <SelectContent>
                    {examTypes.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div>
                <Label className="text-sm">Reason for Rescheduling</Label>
                <Textarea
                  value={rescheduleForm.reason}
                  onChange={(e) => setRescheduleForm({...rescheduleForm, reason: e.target.value})}
                  placeholder="Reason for the change..."
                  rows={2}
                  className="mt-1 rounded-sm"
                />
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <Button type="button" variant="outline" onClick={() => setShowRescheduleModal(false)} className="rounded-sm">
                  Cancel
                </Button>
                <Button type="submit" disabled={processing} className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm">
                  <Calendar className="h-4 w-4 mr-1" />
                  {processing ? 'Processing...' : 'Reschedule'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Change History Modal */}
      <Dialog open={showHistoryModal} onOpenChange={setShowHistoryModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Clock className="h-5 w-5 text-slate-600" />
              Allocation Change History
            </DialogTitle>
          </DialogHeader>
          
          {loadingHistory ? (
            <div className="text-center py-8">
              <p className="text-sm text-slate-500">Loading history...</p>
            </div>
          ) : changeHistory ? (
            <div className="space-y-4">
              {/* Current Allocation Summary */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <h4 className="text-sm font-semibold text-[#1B2A4A] mb-2">Current Allocation</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div><span className="text-slate-500">Candidate:</span> <span className="font-medium">{changeHistory.candidate_name}</span></div>
                  <div><span className="text-slate-500">Status:</span> <Badge className={`text-xs ${
                    changeHistory.current_status === 'scheduled' ? 'bg-green-100 text-green-700' :
                    changeHistory.current_status === 'rescheduled' ? 'bg-amber-100 text-amber-700' :
                    'bg-slate-100 text-slate-700'
                  }`}>{changeHistory.current_status}</Badge></div>
                  <div><span className="text-slate-500">Center:</span> <span className="font-medium">{changeHistory.current_allocation?.examination_center}</span></div>
                  <div><span className="text-slate-500">Examiner:</span> <span className="font-medium">{changeHistory.current_allocation?.examiner}</span></div>
                  <div><span className="text-slate-500">Date:</span> <span className="font-medium">{changeHistory.current_allocation?.examination_date}</span></div>
                  <div><span className="text-slate-500">Time:</span> <span className="font-medium">{changeHistory.current_allocation?.examination_time || 'TBD'}</span></div>
                </div>
              </div>

              {/* Change History Timeline */}
              <div>
                <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3 flex items-center gap-2">
                  Change Timeline
                  <Badge className="bg-slate-100 text-slate-600">{changeHistory.total_changes} reschedule(s)</Badge>
                </h4>
                
                {changeHistory.change_history?.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No changes recorded yet.</p>
                ) : (
                  <div className="space-y-3">
                    {changeHistory.change_history?.map((entry, index) => (
                      <div 
                        key={entry.id || index} 
                        className={`relative pl-6 pb-4 ${
                          index < changeHistory.change_history.length - 1 ? 'border-l-2 border-slate-200' : ''
                        }`}
                      >
                        {/* Timeline dot */}
                        <div className={`absolute left-0 top-0 w-3 h-3 rounded-full -translate-x-[7px] ${
                          entry.type === 'allocation' ? 'bg-green-500' : 'bg-amber-500'
                        }`}></div>
                        
                        {/* Entry content */}
                        <div className="bg-white border rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <Badge className={`text-xs ${
                              entry.type === 'allocation' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {entry.type === 'allocation' ? 'Initial Allocation' : 'Rescheduled'}
                            </Badge>
                            <span className="text-xs text-slate-400">
                              {entry.modified_at ? new Date(entry.modified_at).toLocaleString() : 'N/A'}
                            </span>
                          </div>
                          
                          {/* Changes */}
                          <div className="space-y-1">
                            {entry.changes?.map((change, cIdx) => (
                              <div key={cIdx} className="text-xs flex items-center gap-2">
                                <span className="text-slate-500 capitalize">{change.field?.replace('_', ' ')}:</span>
                                {change.old_value && (
                                  <>
                                    <span className="text-red-500 line-through">{change.old_value}</span>
                                    <span className="text-slate-400">→</span>
                                  </>
                                )}
                                <span className="text-green-600 font-medium">{change.new_value || 'N/A'}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Reason */}
                          {entry.reason && (
                            <div className="mt-2 text-xs text-slate-500 italic bg-slate-50 p-2 rounded">
                              Reason: {entry.reason}
                            </div>
                          )}
                          
                          <p className="text-xs text-slate-400 mt-2">
                            Modified by: {entry.modified_by_name || 'Admin'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-500 text-center py-8">No history available.</p>
          )}
          
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setShowHistoryModal(false)} className="rounded-sm">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Allocation Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-[#1B2A4A] flex items-center gap-2">
              <Users className="h-5 w-5 text-green-600" />
              Bulk Allocate Applications
              <Badge className="bg-blue-100 text-blue-700">{selectedApps.length} selected</Badge>
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleBulkAllocate} className="space-y-4">
            {/* Selected Applications Summary */}
            <div className="bg-slate-50 p-3 rounded-md max-h-32 overflow-y-auto">
              <p className="text-xs font-medium text-[#1B2A4A] mb-2">Selected Candidates:</p>
              <div className="space-y-1">
                {apps.filter(a => selectedApps.includes(a.id)).map(a => (
                  <p key={a.id} className="text-xs text-slate-600">{a.full_name} - {a.email}</p>
                ))}
              </div>
            </div>

            {/* Examination Center */}
            <div>
              <Label className="text-sm">Examination Center *</Label>
              <Select 
                value={bulkForm.examination_center_id}
                onValueChange={(v) => setBulkForm({...bulkForm, examination_center_id: v})}
              >
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder="Select examination center" />
                </SelectTrigger>
                <SelectContent>
                  {activeCenters.map(center => (
                    <SelectItem key={center.id} value={center.id}>
                      {center.name} - {center.city}, {center.country}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Examiner */}
            <div>
              <Label className="text-sm">Examiner *</Label>
              <Select 
                value={bulkForm.examiner_id}
                onValueChange={(v) => setBulkForm({...bulkForm, examiner_id: v})}
              >
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder="Select examiner" />
                </SelectTrigger>
                <SelectContent>
                  {activeExaminers.map(ex => (
                    <SelectItem key={ex.id} value={ex.id}>
                      {ex.full_name} ({ex.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Examination Date *</Label>
                <Input
                  type="date"
                  value={bulkForm.examination_date}
                  onChange={(e) => setBulkForm({...bulkForm, examination_date: e.target.value})}
                  className="mt-1 rounded-sm"
                  required
                />
              </div>
              <div>
                <Label className="text-sm">Examination Time</Label>
                <Input
                  type="time"
                  value={bulkForm.examination_time}
                  onChange={(e) => setBulkForm({...bulkForm, examination_time: e.target.value})}
                  className="mt-1 rounded-sm"
                />
              </div>
            </div>

            {/* Examination Type */}
            <div>
              <Label className="text-sm">Examination Type *</Label>
              <Select 
                value={bulkForm.examination_type}
                onValueChange={(v) => setBulkForm({...bulkForm, examination_type: v})}
              >
                <SelectTrigger className="mt-1 rounded-sm">
                  <SelectValue placeholder="Select examination type" />
                </SelectTrigger>
                <SelectContent>
                  {examTypes.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Admin Notes */}
            <div>
              <Label className="text-sm">Admin Notes (Optional)</Label>
              <Textarea
                value={bulkForm.admin_notes}
                onChange={(e) => setBulkForm({...bulkForm, admin_notes: e.target.value})}
                placeholder="Notes for all allocations..."
                rows={2}
                className="mt-1 rounded-sm"
              />
            </div>

            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="outline" onClick={() => setShowBulkModal(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={processing} className="bg-green-600 hover:bg-green-700 text-white rounded-sm">
                <Users className="h-4 w-4 mr-1" />
                {processing ? 'Processing...' : `Allocate ${selectedApps.length} Applications`}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ApplicationsTab;
